/**
 * TossBallDrawer — the request composer in a wide right-hand drawer, so tossing
 * the ball keeps the cycle or track you were on in view (owner request
 * 2026-11-03). It hosts RequestComposerPage itself (embedded), never a second
 * composer. Opened through useTossDrawerStore; closes on send, on Escape, on
 * the × and when the page changes under it.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTossDrawerStore } from '@/stores/prioritization/tossDrawerStore';
import { RequestComposerPage } from '@/pages/prioritization/catchball/RequestComposerPage';
import { CATCHBALL_LABELS } from '@/lib/catchball/vocabulary';

export function TossBallDrawer() {
  const open = useTossDrawerStore((s) => s.open);
  const close = useTossDrawerStore((s) => s.close);
  const { pathname } = useLocation();
  const openedOn = useRef<string | null>(null);

  useEffect(() => {
    if (open && openedOn.current === null) openedOn.current = pathname;
    if (!open) openedOn.current = null;
    else if (openedOn.current !== pathname) close();
  }, [open, pathname, close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;
  return (
    <>
      <div aria-hidden onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.18)' }} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={CATCHBALL_LABELS.sendTheBall}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(920px, 94vw)',
          zIndex: 71,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--rule)',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.16)',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--rule)', background: 'var(--bg-raised)' }}>
          <span style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-sec)' }}>
            {CATCHBALL_LABELS.sendTheBall}
          </span>
          <button type="button" onClick={close} aria-label="Close" title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-ter)', display: 'flex' }}>
            <X size={16} />
          </button>
        </header>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <RequestComposerPage embedded cycleIdProp={open.cycleId} trackIdProp={open.trackId} onSent={close} />
        </div>
      </aside>
    </>
  );
}
